
/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class ResponsiveUIAdjustmentProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict = pFable;
		this.defaultColumnCount = 12;
		// NOTE: The default column count is set to 12, which is the standard for Bulma CSS framework.
		// This can be adjusted based on the application's requirements, but Bulma supports up to 12 columns by default, so we'll need to expand the Bulma styling collection if we want to support more than that.
		this.activeColumnCount = this.defaultColumnCount;
	}

	/**
	 * Set the active column count for responsive UI adjustments
	 * @param {Number} pColumnCount - The number of columns to set
	 * @return {Number} - The active column count after setting
	 */
	setActiveColumnCount(pColumnCount)
	{
		if (typeof pColumnCount !== 'number' || pColumnCount <= 0)
		{
			this.pict.log.error('Invalid column count provided.');
			throw new Error('Invalid column count provided.');
		}
		// if the column count is greater than the default, we need to adjust it to the default until we can expand the Bulma styling collection
		if (pColumnCount > this.defaultColumnCount)
		{
			this.pict.log.warn(`Requested column count ${pColumnCount} exceeds default column count ${this.defaultColumnCount}. Adjusting to default.`);
			this.pict.log.warn(`DEVELOPER NOTE: Bulma styling collection will need to be expanded to support more than ${this.defaultColumnCount} columns.`);
			pColumnCount = this.defaultColumnCount;
		}
		this.activeColumnCount = pColumnCount;
		return this.activeColumnCount;
	}

	/**
	 * Get the current active column count for responsive UI adjustments
	 * @return {Number} - The current active column count
	 */
	getActiveColumnCount()
	{
		return this.activeColumnCount;
	}

	/**
	 * Reset the active column count to the default value
	 * @return {Number} - The active column count after reset
	 */
	resetActiveColumnCount()
	{
		this.activeColumnCount = this.defaultColumnCount;
		return this.activeColumnCount;
	}

	/**
	 * Determine whether a column should be excluded from the row layout math.
	 *
	 * An excluded column is layout-neutral: it is dropped from every width sum / normalization
	 * so the remaining visible columns fill the row as if it were not present, and the
	 * {~ColumnWidth:~} macro renders it taking no horizontal space.
	 *
	 * A column is excluded when ANY of:
	 *   - its PictForm.Width is explicitly zero (parseFloat === 0) — the deliberate "no space" signal,
	 *   - it is a hidden input type (InputType === 'Hidden' or starting with 'MaterialsHidden'), OR
	 *   - it carries a truthy PictForm.Hidden flag (the Materials input types honor this to hide a field).
	 *
	 * A column with NO Width set (undefined/null/'') is NOT excluded on the width axis — it is a
	 * normal visible column that defaults to a width of 1 elsewhere. Only an explicit zero counts.
	 *
	 * @param {Object} pColumn - The column descriptor to test
	 * @return {Boolean} - Returns true when the column should not occupy layout space
	 */
	isLayoutExcludedColumn(pColumn)
	{
		if (!pColumn || !pColumn.PictForm)
		{
			return false;
		}
		const tmpInputType = pColumn.PictForm.InputType;
		if (typeof tmpInputType === 'string' && (tmpInputType === 'Hidden' || tmpInputType.startsWith('MaterialsHidden')))
		{
			return true;
		}
		// The Materials input types honor an explicit PictForm.Hidden flag to hide a field; treat it as layout-excluded too.
		if (pColumn.PictForm.Hidden)
		{
			return true;
		}
		// Distinguish an explicit zero Width (excluded) from an unset Width (a normal, visible un-widthed column).
		const tmpWidth = pColumn.PictForm.Width;
		if (tmpWidth !== undefined && tmpWidth !== null && tmpWidth !== '' && parseFloat(tmpWidth) === 0)
		{
			return true;
		}
		return false;
	}

	/**
	 * Check if all columns in the row have an empty or undefined PictForm.Width
	 * @param {Object[]} pRowObject - The Pict row object containing columns to check
	 * @return {Boolean} - Returns true if all columns have an empty or undefined PictForm.Width
	 */
	checkRowObjectForAllEmptyWidths(pRowObject)
	{
		// check if all columns in the row have an empty or undefined PictForm.Width
		const allEmpty = pRowObject.every((column) => !column.PictForm || !column.PictForm.Width);
		if (allEmpty)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info('Row has no columns with a PictForm.Width set. Assuming Bulma classes are already balanced.');
			}
			return true;
		}
		// if any column has a PictForm.Width set, we need to check for balance with Bulma classes
		return false;
	}

	/**
	 * Check if the row has any columns with a PictForm.Width set and if they exceed the active column count
	 * @param {Object[]} pRowObject - The Pict row object containing columns to check
	 * @return {Boolean} - Returns true if the row has columns exceeding the active column count, false otherwise
	 */
	checkForOvercrowdingInRow(pRowObject)
	{
		// Check if the row has any columns with a PictForm.Width set and if they exceed the active column count
		// Layout-excluded columns (explicit zero width / hidden inputs) take no space, so they are not counted.
		const totalWidth = pRowObject.reduce((acc, column) =>
		{
			if (this.isLayoutExcludedColumn(column))
			{
				return acc;
			}
			return acc + (column.PictForm.Width ? parseFloat(column.PictForm.Width) : 1);
		}, 0);
		if (totalWidth > this.activeColumnCount)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.warn(`Row columns exceed active column count of ${this.activeColumnCount}. Will adjust columns accordingly.`);
			}
			return true;
		}
		return false;
	}

	/**
	 * Check if the row has any columns with a PictForm.Width set and if they add up to the active column count
	 * @param {Object[]} pRowObject - The Pict row object containing columns to check
	 * @return {Boolean} - Returns true if the row is balanced with Bulma classes, false otherwise
	 */
	checkForBalancedRowsWithBulmaClasses(pRowObject)
	{
		// first, we need to check if the row has any columns with a PictForm.Width set (ignoring layout-excluded columns)
		const hasWidths = pRowObject.some((column) => column.PictForm && column.PictForm.Width && !this.isLayoutExcludedColumn(column));
		if (!hasWidths)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info('Row does not have any columns with a PictForm.Width set. Assuming Bulma classes are already balanced.');
			}
			return true;
		}
		// next, we need to check if the total widths and the width offsets of the columns add up to the active column count
		// Layout-excluded columns (explicit zero width / hidden inputs) take no space, so they are not counted.
		const totalWidth = pRowObject.reduce((acc, column) =>
		{
			if (this.isLayoutExcludedColumn(column))
			{
				return acc;
			}
			return acc + (column.PictForm.Width ? parseFloat(column.PictForm.Width) : 1);
		}, 0);
		const totalOffset = pRowObject.reduce((acc, column) =>
		{
			if (this.isLayoutExcludedColumn(column))
			{
				return acc;
			}
			return acc + (column.PictForm.WidthOffset ? parseFloat(column.PictForm.WidthOffset) : 0);
		}, 0);
		if (totalWidth + totalOffset === this.activeColumnCount)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info(`Row columns add up to the active column count of ${this.activeColumnCount}. Assuming Bulma classes are already balanced.`);
			}
			return true;
		}
		if (this.pict.LogNoisiness > 2)
		{
			this.pict.log.warn(`Row columns do not add up to the active column count of ${this.activeColumnCount}. Will adjust columns accordingly.`);
		}
		return false;
	}

	/**
	 * Balance the columns in the row with Bulma classes based on their widths
	 * @param {Object[]} pRowObject - The Pict row object containing columns to balance
	 * @return {Object[]} - The modified row object with Bulma classes applied
	 */
	balanceRowsWithBulmaClasses(pRowObject)
	{
		pRowObject.forEach((column) =>
		{
			// A layout-excluded column must never emit is-0 (invalid Bulma); mark it so the macro renders no width.
			if (this.isLayoutExcludedColumn(column))
			{
				column.PictForm._LayoutExcluded = true;
				return;
			}
			column.PictForm._BulmaColumnClasses = `is-${column.PictForm.Width}`;
			// if the column has a WidthOffset, we need to add that to the Bulma classes as well for the column offset
			if (column.PictForm.WidthOffset)
			{
				column.PictForm._BulmaColumnClasses += ` is-offset-${column.PictForm.WidthOffset}`;
			}
		});
		return pRowObject;
	}

	/**
	 * Balance the columns for a given row based on the active column count
	 * @param {Object} pRowObject - The Pict row object containing columns to balance
	 */
	balanceRowsWithCalculatedWidthPercentages(pRowObject)
	{
		const expectedColumnWidths = {};
		// first: get all the set Width values for each column in the row so what we know what the expected widths are.
		// Layout-excluded columns (explicit zero width / hidden inputs) take no space, so they contribute a width of 0.
		for (let i = 0; i < pRowObject.length; i++)
		{
			if (this.isLayoutExcludedColumn(pRowObject[i]))
			{
				expectedColumnWidths[i] = '0';
			}
			else
			{
				expectedColumnWidths[i] = pRowObject[i]?.PictForm?.Width || '1';
			}
		}
		// next: for each expected width, we need to adjust the column width based on the active column count
		const totalColumnCount = Object.keys(expectedColumnWidths).length;
		const minimumColumnPercentage = (1 / this.activeColumnCount * 100);
		// the sum of all visible widths is constant for the row; an all-excluded row sums to 0 (guard against divide-by-zero).
		const sumOfWidths = Object.values(expectedColumnWidths).reduce((acc, width) => acc + parseFloat(width), 0);
		// if the total column count is less than the active column count, we need to adjust the widths accordingly
		for (let i = 0; i < totalColumnCount; i++)
		{
			const column = pRowObject[i];
			// An excluded column must never emit is-0 (invalid Bulma) or steal a minimum-width slot; mark it for no-space render.
			if (this.isLayoutExcludedColumn(column))
			{
				column.PictForm._LayoutExcluded = true;
				continue;
			}
			if (sumOfWidths <= 0)
			{
				continue;
			}
			column.PictForm._AdjustedWidthPercentage = (parseFloat(expectedColumnWidths[i]) / sumOfWidths * 100);
			// if the adjusted width percentage is less than the minimum, we need to set it to the minimum
			if (column.PictForm._AdjustedWidthPercentage < minimumColumnPercentage)
			{
				if (this.pict.LogNoisiness > 2)
				{
					this.pict.log.warn(`Column ${column.PictForm.Name} has an adjusted width percentage of ${column.PictForm._AdjustedWidthPercentage}%, which is less than the minimum of ${minimumColumnPercentage}%. Adjusting to minimum.`);
				}
				column.PictForm._AdjustedWidthPercentage = minimumColumnPercentage;
			}
		}
		// Return the modified row
		return pRowObject;
	}

	/**
	 * Map all rows by their sections and adjust the columns based on the active column count
	 * @return {Boolean} - Returns true if the mapping was successful
	 */
	mapAllRowsBySections(pDescriptors, pSections)
	{
		if (!pDescriptors || typeof pDescriptors !== 'object')
		{
			this.pict.log.error('Invalid descriptors provided for mapping rows by sections.');
			throw new Error('Invalid descriptors provided for mapping rows by sections.');
		}
		const tmpColumns = pDescriptors;
		// map the column by their Section and Row
		const tmpRowsBySection = {};
		Object.values(tmpColumns).forEach((column) =>
		{
			if (!column.PictForm || !column.PictForm.Section)
			{
				if (this.pict.LogNoisiness > 2)
				{
					this.pict.log.warn(`Column ${column.Name} does not have a valid Section defined. Skipping.`);
				}
				return;
			}
			const tmpRowOrdinal = column.PictForm.Row || '1'; // default to row 1 if not defined
			// Objects, not arrays: rows are keyed by Row as written ("1.5", "8.0"), which are not array indexes.
			if (!tmpRowsBySection[column.PictForm.Section])
			{
				tmpRowsBySection[column.PictForm.Section] = {};
			}
			if (!tmpRowsBySection[column.PictForm.Section][column.PictForm.Group])
			{
				tmpRowsBySection[column.PictForm.Section][column.PictForm.Group] = {};
			}
			if (!tmpRowsBySection[column.PictForm.Section][column.PictForm.Group][tmpRowOrdinal])
			{
				tmpRowsBySection[column.PictForm.Section][column.PictForm.Group][tmpRowOrdinal] = [];
			}
			tmpRowsBySection[column.PictForm.Section][column.PictForm.Group][tmpRowOrdinal].push(column);
		});
		// now we need to adjust the columns for each row based on the active column count
		for (const section in tmpRowsBySection)
		{
			if (Object.prototype.hasOwnProperty.call(tmpRowsBySection, section))
			{
				// now we need to iterate over each row in the section and adjust the columns based on the active column count
				if (!tmpRowsBySection[section] || !Object.keys(tmpRowsBySection[section]).length)
				{
					if (this.pict.LogNoisiness > 2)
					{
						this.pict.log.warn(`Section ${section} does not have any rows defined. Skipping adjustment.`);
					}
					continue;
				}

				for (const group in tmpRowsBySection[section])
				{
					if (Object.prototype.hasOwnProperty.call(tmpRowsBySection[section], group))
					{
						const groupObject = tmpRowsBySection[section][group];

						for (const row in groupObject)
						{
							if (Object.prototype.hasOwnProperty.call(groupObject, row))
							{
								const rowObject = groupObject[row];
								// check if the row overcrowded before we do any adjustments, so we can update the parent "columns" wrapper
								if (this.checkForOvercrowdingInRow(rowObject))
								{
									if (this.pict.LogNoisiness > 2)
									{
										this.pict.log.warn(`Row ${row} in section ${section} is overcrowded. Adjusting columns.`);
									}
									// NOTE: we mark the entire group as overcrowded, so we can adjust the parent "columns" wrappers
									// This doesn't negatively impact any non-overcrowded rows, as they will be balanced with Bulma classes, however,
									// it would be better to mark the row itself as overcrowded, but that would require a change in the PictForm structure
									this.markGroupAsOvercrowded(rowObject, pSections, section, group);
								}
								// check if the row has all empty widths
								if (this.checkRowObjectForAllEmptyWidths(rowObject))
								{
									if (this.pict.LogNoisiness > 2)
									{
										this.pict.log.info(`Row ${row} in section ${section} is already balanced with Bulma classes. Skipping adjustment.`);
									}
									continue;
								}
								// check if the row is balanced with Bulma classes
								if (this.checkForBalancedRowsWithBulmaClasses(rowObject))
								{
									if (this.pict.LogNoisiness > 2)
									{
										this.pict.log.info(`Row ${row} in section ${section} is already balanced. Applying Bulma classes.`);
									}
									// just balance the row with existing Bulma classes from the PictForm.Width and PictForm.WidthOffset
									this.balanceRowsWithBulmaClasses(rowObject);
									continue;
								}
								// balance the row with calculated width percentages
								this.balanceRowsWithCalculatedWidthPercentages(rowObject);
							}
						}
						// now we need to set the adjusted rows back to the section
						tmpRowsBySection[section][group] = Object.values(groupObject).flat();
					}
				}
				// now we need to set the adjusted rows back to the section
				tmpRowsBySection[section] = Object.values(tmpRowsBySection[section]).map((group) =>
				{
					return Object.values(group).flat();
				});
			}
		}
		// temp log the adjusted rows by section
		if (this.pict.LogNoisiness > 2)
		{
			this.pict.log.info('Adjusted rows by section:', tmpRowsBySection);
		}
		return true;
	}

	/**
	 * Mark the row as overcrowded by adding a PictForm property
	 * @param {Object[]} pRowObject - The Pict row object containing columns to mark as overcrowded
	 * @param {Object} pSectionsObject - The Pict sections object containing the section information
	 * @param {String} pSectionName - The name of the section containing the row
	 * @param {String} pGroupName - The name of the group containing the row
	 * @return {Object} - The modified group object with overcrowded columns marked
	 */
	markGroupAsOvercrowded(pRowObject, pSectionsObject, pSectionName, pGroupName)
	{
		// mark the row as overcrowded by adding a PictForm property
		if (!pRowObject || !Array.isArray(pRowObject))
		{
			this.pict.log.error('Invalid row object provided for marking as overcrowded.');
			throw new Error('Invalid row object provided for marking as overcrowded.');
		}
		if (!pSectionsObject || typeof pSectionsObject !== 'object')
		{
			this.pict.log.error('Invalid sections object provided for marking as overcrowded.');
			throw new Error('Invalid sections object provided for marking as overcrowded.');
		}
		if (!pSectionName || typeof pSectionName !== 'string')
		{
			this.pict.log.error('Invalid section name provided for marking as overcrowded.');
			pSectionName = 'UNSET_SECTION'; // fallback to default section name if not provided
		}
		if (!pGroupName || typeof pGroupName !== 'string')
		{
			this.pict.log.error('Invalid group name provided for marking as overcrowded.');
			pGroupName = 'UNSET_GROUP'; // fallback to default group name if not provided
		}
		// find the section and group in the sections object
		const tmpSection = pSectionsObject.find((section) => section.Hash === pSectionName);
		if (!tmpSection)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.warn(`Section ${pSectionName} not found in sections object.`);
			}
			return {};
		}
		const pGroupObject = tmpSection.Groups.find((group) => group.Hash === pGroupName);
		if (!pGroupObject)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.warn(`Group ${pGroupName} not found in section ${pSectionName}.`);
			}
			return {};
		}
		// now we need to mark the row as overcrowded by adding a PictForm property
		if (!pGroupObject._OvercrowdedColumns)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info(`Marking group ${pGroupName} in section ${pSectionName} as overcrowded.`);
			}
			pGroupObject._OvercrowdedColumns = true;
		}
		else
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info(`Group ${pGroupName} in section ${pSectionName} is already marked as overcrowded.`);
			}
		}
		// check if the row is overcrowded and set the _OvercrowdedColumns property
		return pGroupObject;
	}

	/**
	 * Called after the provider is initialized, this method will adjust the UI based on the active column count
	 * @param {Function} [pCallback] - Optional callback to execute after initialization
	 * @return {Promise} - A promise that resolves when the initialization is complete
	 * @async
	 */
	onInitializeAsync(pCallback)
	{
		// don't break if sub-dynamic forms are using the correct pattern yet
		if (!this.pict || !this.pict.settings || !this.pict.settings.MyDefaultFormManifest || !this.pict.settings.MyDefaultFormManifest.Descriptors)
		{
			this.pict.log.warn('Pict settings or descriptors are not properly initialized to run ResponsiveUIAdjustmentProvider.');
			// if the callback is provided, we can call it to continue the initialization process
			if (typeof pCallback === 'function')
			{
				return pCallback();
			}
			return Promise.resolve();
		}
		// set the active column count based on the Pict settings, if available
		this.pict.log.info('ResponsiveUIAdjustmentProvider initialized with active column count:', this.activeColumnCount);
		// for every row in the content, we need to adjust the columns based on the active column count
		// This will add either a '_BulmaColumnClasses' or '_AdjustedWidthPercentage' value to each column, unless all widths are empty, then we let Bulma handle the widths
		this.mapAllRowsBySections(this.pict.settings?.MyDefaultFormManifest?.Descriptors, this.pict.settings?.MyDefaultFormManifest?.Sections);

		// add a resize event listener to the window to adjust the UI elements when the window is resized
		const resizeDebounce = 100; // debounce time in milliseconds
		let resizeTimeout;
		// NOTE: We use a debounce to prevent excessive calls to the resize handler, which can be expensive in terms of performance
		window.addEventListener('resize', () =>
		{
			if (resizeTimeout)
			{
				clearTimeout(resizeTimeout);
			}
			resizeTimeout = setTimeout(() =>
			{
				this.onWindowResize();
			}, resizeDebounce);
		});
		this.pict.log.info('ResponsiveUIAdjustmentProvider: Window resize event listener added.');
		// also, let's run it once to adjust the UI elements immediately
		this.onWindowResize();

		if (typeof pCallback === 'function')
		{
			return pCallback();
		}
		return Promise.resolve();
	}

	/**
	 * Called when the window is resized, this method will adjust all the labels to be a consistent height across all columns in the row
	 * @return {void}
	 */
	onWindowResize()
	{
		// go through all sections and groups, and adjust the label heights to be consistent for all columns in the row
		this.pict.log.info('ResponsiveUIAdjustmentProvider: Window resized, adjusting UI elements.');
		const allColumnRows = document.querySelectorAll('.columns:not(.is-hidden)');
		allColumnRows.forEach((row) =>
		{
			const columns = row.querySelectorAll('.column');
			if (columns.length > 0)
			{
				let maxHeight = 0;
				// find the maximum height of the labels in the columns
				columns.forEach((column) =>
				{
					const label = column.querySelector('label');
					if (label && label.offsetHeight > maxHeight)
					{
						maxHeight = label.offsetHeight;
					}
				});
				// if the maximum height is greater than 0, we can set the height of all labels to the maximum height
				if (maxHeight > 0)
				{
					columns.forEach((column) =>
					{
						const label = column.querySelector('label');
						if (label)
						{
							label.style.height = `${maxHeight}px`;
						}
					});
				}
			}
		});
		// A group can cross the "big enough to warrant an inline/modal choice"
		// threshold purely because the window got shorter, so ride this already
		// debounced resize pass rather than registering another listener.
		// NOTE: refreshEntryModeToggleVisibility must never call back into
		// prepareAnyExtraTableControls -- that would recurse through here.
		const tmpTableControls = this.pict.providers['Bulma-TableControls'];
		if (tmpTableControls && (typeof tmpTableControls.refreshEntryModeToggleVisibility === 'function'))
		{
			tmpTableControls.refreshEntryModeToggleVisibility();
		}
	}
}
module.exports = ResponsiveUIAdjustmentProvider;
